// BM-H4761 (Vanessa, 26 Oct 2026): the New memo button on the matter page.
//
// One click opens this picker with the heading block already filled in from
// the matter (backend: hub/matter_memo.py). The fee earner picks who it is to
// and the marking, and gets the firm's approved memo template, named and filed
// in the matter's SharePoint folder, logged on the matter and opened in Word.
//
// Every field the matter supplied can be changed here: a pre-CaseMinder matter
// often has no client or fee earner on record, and a guess in a privileged
// document is worse than a blank box. When SharePoint cannot be reached the
// memo is offered as a download instead, and the screen says it was not filed.
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FileText, Loader2, Download, ExternalLink } from "lucide-react";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";

function newRequestId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// Mirrors hub/matter_memo.memo_filename, so the name shown is the name filed.
export function memoFilename(toFirst, clientShort, matterRef, today = new Date()) {
  const last4 = (matterRef || "").replace(/\D/g, "").slice(-4) || matterRef;
  const dd = String(today.getDate()).padStart(2, "0");
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const name = `Memo to ${toFirst} - ${clientShort} - ${last4} - ${dd}.${mm}.${today.getFullYear()}.docx`;
  return name.replace(/[–—]/g, "-").replace(/[\\/:*?"<>|#%]/g, "").replace(/\s+/g, " ").trim();
}

const selectCls = "w-full rounded-md border border-white/10 bg-bonsai-bg px-3 py-2 text-sm text-white";

export default function NewMemoDialog({ matterRef }) {
  const [open, setOpen] = useState(false);
  const [defaults, setDefaults] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [toEmail, setToEmail] = useState("");
  const [ccEmails, setCcEmails] = useState([]);
  const [marking, setMarking] = useState("");
  const [clientName, setClientName] = useState("");
  const [clientShort, setClientShort] = useState("");
  const [point, setPoint] = useState("");
  const [keyDateIds, setKeyDateIds] = useState([]);
  const [requestId, setRequestId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [filed, setFiled] = useState(null);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setDefaults(null); setLoadError(""); setError(""); setFiled(null); setPoint("");
    setRequestId(newRequestId());
    axios.get(`${API}/matter-memo/${encodeURIComponent(matterRef)}/defaults`)
      .then(({ data }) => {
        if (!active) return;
        setDefaults(data);
        setToEmail(data.default_to || "");
        setCcEmails([]);
        setMarking(data.marking || "");
        setClientName(data.client_name || "");
        setClientShort(data.client_short || "");
        setKeyDateIds([]);
      })
      .catch(err => active && setLoadError(errorMessage(err, "The matter's details could not be read.")));
    return () => { active = false; };
  }, [open, matterRef]);

  const staff = useMemo(() => defaults?.staff || [], [defaults]);
  const to = staff.find(s => s.email === toEmail);
  const filename = to && clientShort.trim()
    ? memoFilename(to.name.split(" ")[0], clientShort.trim(), matterRef) : "";
  const ready = Boolean(to && marking && clientName.trim() && clientShort.trim()) && !busy;

  const payload = () => ({
    request_id: requestId, to_email: toEmail, cc_emails: ccEmails, marking,
    client_name: clientName, client_short: clientShort, subject_point: point,
    key_date_ids: keyDateIds,
  });

  const toggle = (list, setList, value) =>
    setList(list.includes(value) ? list.filter(v => v !== value) : [...list, value]);

  const create = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/matter-memo/${encodeURIComponent(matterRef)}`, payload());
      setFiled(data);
      // Open it in Word. A hidden frame hands the ms-word: link to Word
      // without navigating CaseMinder away; the buttons below do it by hand.
      if (data.open_in_word_url) {
        const frame = document.createElement("iframe");
        frame.style.display = "none";
        frame.src = data.open_in_word_url;
        document.body.appendChild(frame);
        setTimeout(() => frame.remove(), 5000);
      }
    } catch (err) {
      setError(errorMessage(err, "The memo could not be created."));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/matter-memo/${encodeURIComponent(matterRef)}/download`,
        payload(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "Memo.docx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      // A blob response carries the server's reason as text; read it out.
      let detail = "";
      try { detail = JSON.parse(await err.response.data.text()).detail; } catch (e) { detail = ""; }
      setError(typeof detail === "string" && detail ? detail : errorMessage(err, "The memo could not be downloaded either."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="matter-new-memo-btn">
        <FileText className="w-3.5 h-3.5 mr-1.5" /> New memo
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="new-memo-dialog">
          <DialogHeader>
            <DialogTitle>New memo</DialogTitle>
            <DialogDescription>
              The firm's memo template with the heading block filled in, filed in this matter's
              SharePoint folder and opened in Word for you to write.
            </DialogDescription>
          </DialogHeader>

          {!defaults && !loadError && (
            <p className="text-sm text-slate-400 flex items-center gap-2" data-testid="new-memo-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Reading the matter
            </p>
          )}
          {loadError && <p className="text-sm text-status-red" data-testid="new-memo-load-error">{loadError}</p>}

          {defaults && !filed && (
            <div className="space-y-4 text-sm">
              <p className="text-slate-400">
                From <span className="text-white">{defaults.from?.name}</span>, {defaults.date}, matter{" "}
                <span className="font-mono text-teal">{defaults.matter_reference}</span>
              </p>

              <label className="block space-y-1">
                <span className="font-semibold">To</span>
                <select className={selectCls} value={toEmail} onChange={e => setToEmail(e.target.value)}
                        data-testid="new-memo-to">
                  <option value="">Pick who it is to</option>
                  {staff.map(s => <option key={s.email} value={s.email}>{s.name}{s.title ? ` (${s.title})` : ""}</option>)}
                </select>
              </label>

              <details>
                <summary className="cursor-pointer font-semibold">Cc{ccEmails.length ? ` (${ccEmails.length})` : " (optional)"}</summary>
                <div className="mt-2 grid grid-cols-2 gap-1" data-testid="new-memo-cc">
                  {staff.filter(s => s.email !== toEmail && s.email !== defaults.from?.email).map(s => (
                    <label key={s.email} className="flex items-center gap-2">
                      <input type="checkbox" checked={ccEmails.includes(s.email)}
                             onChange={() => toggle(ccEmails, setCcEmails, s.email)} />
                      {s.name}
                    </label>
                  ))}
                </div>
              </details>

              <fieldset className="space-y-1" data-testid="new-memo-marking">
                <legend className="font-semibold">Marking</legend>
                {(defaults.markings || []).map(m => (
                  <label key={m.key} className="flex items-center gap-2">
                    <input type="radio" name="memo-marking" value={m.key} checked={marking === m.key}
                           onChange={() => setMarking(m.key)} data-testid={`new-memo-marking-${m.key}`} />
                    {m.label}
                  </label>
                ))}
              </fieldset>

              <label className="block space-y-1">
                <span className="font-semibold">Client</span>
                <Input value={clientName} onChange={e => setClientName(e.target.value)}
                       placeholder="Client name" data-testid="new-memo-client" />
              </label>

              <div className="space-y-1">
                <span className="font-semibold">Subject</span>
                <div className="flex gap-2 items-center">
                  <Input className="w-40" value={clientShort} onChange={e => setClientShort(e.target.value)}
                         placeholder="Short name" data-testid="new-memo-client-short" />
                  <span>:</span>
                  <Input value={point} onChange={e => setPoint(e.target.value)}
                         placeholder="the point (or leave for later)" data-testid="new-memo-point" />
                </div>
              </div>

              {defaults.key_dates_error && (
                <p className="text-status-red" data-testid="new-memo-key-dates-error">{defaults.key_dates_error}</p>
              )}
              {(defaults.key_dates || []).length > 0 && (
                <fieldset className="space-y-1" data-testid="new-memo-key-dates">
                  <legend className="font-semibold">Key dates to include (optional)</legend>
                  {defaults.key_dates.map(k => (
                    <label key={k.id} className="flex items-start gap-2">
                      <input type="checkbox" className="mt-1" checked={keyDateIds.includes(k.id)}
                             onChange={() => toggle(keyDateIds, setKeyDateIds, k.id)} />
                      <span><span className={k.hard ? "font-semibold" : ""}>{k.date}</span>: {k.what}{k.who ? ` (${k.who})` : ""}</span>
                    </label>
                  ))}
                </fieldset>
              )}

              {filename && (
                <p className="text-slate-400" data-testid="new-memo-filename">
                  Will be filed as <span className="text-white">{filename}</span>
                </p>
              )}
              {error && (
                <div className="p-3 rounded-md bg-status-red/10 border border-status-red/30 text-status-red space-y-2"
                     data-testid="new-memo-error">
                  <p>{error}</p>
                  <Button type="button" variant="outline" size="sm" onClick={download} disabled={busy}
                          data-testid="new-memo-download-btn">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download instead (not filed)
                  </Button>
                </div>
              )}
            </div>
          )}

          {filed && (
            <div className="space-y-3 text-sm" data-testid="new-memo-filed">
              <p>
                Filed in <span className="text-white">{filed.folder_path}</span> as{" "}
                <span className="text-white">{filed.filename}</span>, and logged on the matter.
              </p>
              <div className="flex flex-wrap gap-2">
                <a href={filed.open_in_word_url}>
                  <Button size="sm"><FileText className="w-3.5 h-3.5 mr-1.5" /> Open in Word</Button>
                </a>
                <a href={filed.web_url} target="_blank" rel="noreferrer">
                  <Button size="sm" variant="outline"><ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Open in browser</Button>
                </a>
              </div>
            </div>
          )}

          <DialogFooter>
            {!filed ? (
              <Button onClick={create} disabled={!ready} data-testid="new-memo-create-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Create memo
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
